import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Cube, Storefront, ShoppingCart } from "@phosphor-icons/react";
import { LOGO } from "@/components/Navbar";
import { TermsDialog } from "@/components/TermsDialog";

export default function Register() {
  const { register, apiError } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "seller" ? "seller" : "buyer");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const termsKey = role === "seller" ? "sellerTerms" : "buyerTerms";
  const termsLabel = role === "seller" ? "Seller Terms and Conditions" : "Buyer Terms and Conditions";

  const chooseRole = (value) => { setRole(value); setAccepted(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { toast.error(`Please read and accept the ${termsLabel} to continue`); return; }
    setErr(""); setLoading(true);
    try {
      const u = await register({ ...form, role, accepted_terms: true });
      toast.success("Account created. Welcome to SparesHub!");
      nav(u.role === "seller" ? "/seller" : "/account");
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const RoleCard = ({ value, icon: Icon, title, desc }) => (
    <button type="button" onClick={() => chooseRole(value)} data-testid={`role-${value}`} className={`flex items-start gap-3 rounded-sm border p-4 text-left transition-colors ${role === value ? "border-brand bg-brand-50" : "border-border bg-white hover:border-navy/30"}`}>
      <Icon weight={role === value ? "fill" : "regular"} className={`mt-0.5 h-6 w-6 shrink-0 ${role === value ? "text-brand" : "text-navy"}`} />
      <div>
        <div className="font-display font-semibold text-navy">{title}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
    </button>
  );

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-black tracking-tight text-navy">Create your account</h1>
          <p className="mt-2 text-muted-foreground">Already registered? <Link to="/login" className="font-semibold text-brand hover:underline">Log in</Link></p>

          {err && <div className="mt-6 rounded-sm border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="register-error">{err}</div>}

          <div className="mt-6 grid grid-cols-2 gap-3">
            <RoleCard value="buyer" icon={ShoppingCart} title="I'm a Buyer" desc="Browse & purchase bulk lots" />
            <RoleCard value="seller" icon={Storefront} title="I'm a Seller" desc="List lots for liquidation" />
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label>Full name</Label>
              <Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm" placeholder="Your name" />
            </div>
            <div>
              <Label>Company</Label>
              <Input data-testid="register-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 rounded-sm" placeholder="Company / business name" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Email</Label>
                <Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-sm" placeholder="you@company.com" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="register-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-sm" placeholder="+91 ..." />
              </div>
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 rounded-sm" placeholder="Min 6 characters" />
            </div>
            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-navy" data-testid="accept-terms-label">
              <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(Boolean(v))} className="mt-0.5" data-testid="accept-terms-checkbox" />
              <span>
                I have read and agree to the{" "}
                <button type="button" onClick={() => setTermsOpen(true)} className="font-semibold text-brand hover:underline" data-testid="open-terms-link">{termsLabel}</button>.
              </span>
            </label>
            <Button type="submit" disabled={loading || !accepted} className="h-11 w-full rounded-sm bg-brand text-base font-semibold text-white hover:bg-brand-hover disabled:opacity-60" data-testid="register-submit">{loading ? "Creating account..." : `Register as ${role}`}</Button>
          </form>
        </div>
      </div>

      <TermsDialog open={termsOpen} onOpenChange={setTermsOpen} docKey={termsKey} onAccept={() => { setAccepted(true); setTermsOpen(false); }} />

      <div className="hidden flex-col justify-between bg-navy p-12 text-white lg:flex bp-grid">
        <Link to="/" className="flex items-center gap-2 self-end">
          <span className="inline-flex rounded-md bg-white px-3 py-2"><img src={LOGO} alt="SparesHub" className="h-7 w-auto" /></span>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-black leading-tight tracking-tighter">Join India's managed<br /><span className="text-brand">liquidation network.</span></h2>
          <p className="mt-4 max-w-sm text-white/60">Every lot verified. Every file sanitized. Every deal escrow-protected.</p>
        </div>
        <p className="text-xs text-white/40">Bulk B2B only · Minimum lot value ₹5 lakh</p>
      </div>
    </div>
  );
}
